import { computed, ref, watch } from 'vue';
import { useLocalStorage } from './useLocalStorage';
import { DISPLAY_DENSITIES, type DisplayDensity } from '@apparatix/types';

type Options = {
  /** Same prefix the page passes to ColumnDisplaySelector, e.g. 'warehouse:inventory'. */
  storagePrefix: string;
  defaultDensity?: DisplayDensity;
};

/** PrimeVue DataTable `size` per density. Default is the theme default, so undefined. */
export const DENSITY_TABLE_SIZE: Record<DisplayDensity, 'small' | 'large' | undefined> = {
  compact: 'small',
  default: undefined,
  roomy: 'large',
};

/** Body text class per density, for cells that set their own font size (TextTruncate). */
export const DENSITY_TEXT_CLASS: Record<DisplayDensity, string> = {
  compact: 'text-xs',
  default: 'text-sm',
  roomy: 'text-base',
};

/**
 * Multiplier for declared column widths (and saved ones, via useColumnWidths' widthScale), so
 * Compact fits more columns on screen rather than only shortening rows.
 */
export const DENSITY_WIDTH_SCALE: Record<DisplayDensity, number> = {
  compact: 0.8,
  default: 1,
  roomy: 1.2,
};

/**
 * Uniform table row height in px per density, for virtual scrolling: thumbnail height (32/56/112)
 * plus the theme's vertical cell padding (sm 2px, default 8px, lg 12px, each side) plus the 1px border.
 * Rows are also pinned to this height in CSS so hiding the Image column does not change it.
 */
export const DENSITY_ROW_HEIGHT: Record<DisplayDensity, number> = {
  compact: 37,
  default: 73,
  roomy: 137,
};

const isDensity = (value: unknown): value is DisplayDensity =>
  typeof value === 'string' && (DISPLAY_DENSITIES as readonly string[]).includes(value);

/**
 * Screen-level display density persisted in localStorage under `${storagePrefix}:density`,
 * alongside the column keys ColumnDisplaySelector writes for the same prefix.
 */
export const useDisplayDensity = (options: Options) => {
  const { getItem, setItem } = useLocalStorage();
  const storageKey = `${options.storagePrefix}:density`;
  const stored = getItem<string>(storageKey);

  const density = ref<DisplayDensity>(isDensity(stored) ? stored : (options.defaultDensity ?? 'default'));

  watch(density, value => setItem({ key: storageKey, value }));

  const widthScale = computed(() => DENSITY_WIDTH_SCALE[density.value]);

  /** A column width declared in rem for Normal density, scaled for the current one: `columnWidth(16)` → '12.8rem' in Compact. */
  const columnWidth = (rem: number): string => `${Math.round(rem * widthScale.value * 100) / 100}rem`;

  const tableSize = computed(() => DENSITY_TABLE_SIZE[density.value]);

  return {
    density,
    tableSize,
    textClass: computed(() => DENSITY_TEXT_CLASS[density.value]),
    widthScale,
    columnWidth,
    /** PrimeVue `size` for the table's own controls (filter-row inputs): the same scale as the table. */
    controlSize: tableSize,
    rowHeight: computed(() => DENSITY_ROW_HEIGHT[density.value]),
  };
};
